import { useState } from 'react';
import { Card } from '../../../../layout/Card.jsx';
import { Icon } from '../../../../layout/Icon.jsx';
import { Pill } from '../../../../layout/Pill.jsx';
import { formatNumber } from '../../../../shared/format.js';
import { useSystemSummary, useSeedDemo, useWipeData, useResetSystem } from '../../hooks/useSystem.js';
import { ConfirmSeedModal } from './ConfirmSeedModal.jsx';
import { ConfirmWipeModal } from './ConfirmWipeModal.jsx';
import { ConfirmResetModal } from './ConfirmResetModal.jsx';
import { BackupCard } from '../backup/BackupCard.jsx';

const COUNTERS = [
  { key: 'guardians', label: 'أولياء الأمور', icon: 'reception', tone: 'cyan' },
  { key: 'children', label: 'الأطفال', icon: 'children', tone: 'orange' },
  { key: 'cards', label: 'الكروت', icon: 'card', tone: 'pink' },
  { key: 'playsessions', label: 'الجلسات', icon: 'clock', tone: 'green' },
  { key: 'products', label: 'المنتجات', icon: 'products', tone: 'purple' },
  { key: 'warehouses', label: 'المخازن', icon: 'inventory', tone: 'orange' },
  { key: 'salesinvoices', label: 'فواتير البيع', icon: 'pos', tone: 'yellow' },
  { key: 'purchaseinvoices', label: 'فواتير الشراء', icon: 'purchase', tone: 'cyan' },
  { key: 'expenses', label: 'المصاريف', icon: 'finance', tone: 'red' },
  { key: 'auditlogs', label: 'سجل التدقيق', icon: 'reports', tone: 'muted' }
];

const TONE_COLORS = {
  orange: '#FF7A1A',
  cyan: '#06B6D4',
  pink: '#EC4899',
  green: '#22C55E',
  purple: '#8B5CF6',
  yellow: '#FFB627',
  red: '#EF4444',
  muted: '#9CA3AF'
};

function CounterCard({ icon, label, count, tone, loading }) {
  return (
    <div className="bg-white border border-line rounded-[12px] p-3 flex items-center gap-3 shadow-card">
      <div
        className="w-10 h-10 rounded-[10px] grid place-items-center text-white flex-shrink-0"
        style={{ background: TONE_COLORS[tone] || TONE_COLORS.muted }}
      >
        <Icon name={icon} size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-ink-3 font-semibold">{label}</div>
        <div className="text-[20px] font-extrabold tracking-tight">
          {loading ? <span className="text-ink-3 text-[14px]">…</span> : formatNumber(count || 0)}
        </div>
      </div>
    </div>
  );
}

export function SystemTab() {
  const summaryQ = useSystemSummary();
  const seedM = useSeedDemo();
  const wipeM = useWipeData();
  const resetM = useResetSystem();
  const [showSeed, setShowSeed] = useState(false);
  const [showWipe, setShowWipe] = useState(false);
  const [showReset, setShowReset] = useState(false);

  const summary = summaryQ.data || {};
  const hasData = (summary.guardians || 0) > 0 || (summary.products || 0) > 0;

  return (
    <div className="space-y-4">
      {/* Counters */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-extrabold text-[15px] m-0">بيانات النظام الحالية</h3>
            <div className="text-ink-3 text-[12px] mt-0.5">
              {hasData ? 'يوجد بيانات في النظام' : 'النظام فارغ — جاهز لاستقبال بيانات تجريبية أو فعلية'}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => summaryQ.refetch()}
            disabled={summaryQ.isFetching}
          >
            تحديث
          </button>
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {COUNTERS.map((c) => (
            <CounterCard
              key={c.key}
              icon={c.icon}
              label={c.label}
              count={summary[c.key]}
              tone={c.tone}
              loading={summaryQ.isLoading}
            />
          ))}
        </div>
      </Card>

      {/* Full backup / restore */}
      <BackupCard />

      {/* Demo seed card */}
      <Card>
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-[12px] grid place-items-center text-white flex-shrink-0 text-[24px]"
            style={{ background: 'linear-gradient(135deg,#22C55E,#16A34A)' }}
          >
            🎈
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-extrabold text-[15px] m-0">إنشاء بيانات تجريبية كاملة</h3>
              <Pill variant="success">عراقية</Pill>
            </div>
            <p className="text-[12.5px] text-ink-2 leading-relaxed mb-2">
              يضيف بيانات تجريبية متكاملة: 3 مخازن، 8 أقسام، 23 منتجاً بأسعار عراقية،
              4 موردين، 12 ولي أمر، 18 طفلاً، 25 كارت RFID، 5 جلسات نشطة، 3 جلسات
              منتهية مع فواتيرها، 5 فواتير بيع POS، فاتورة شراء واحدة، و3 مصاريف.
              مفيدة لاختبار النظام قبل البدء الفعلي.
            </p>
            <div className="text-[11.5px] text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-[8px] px-3 py-2 mb-3">
              ⚠️ يجب أن يكون النظام فارغاً (بدون بيانات فعلية). إن وُجدت بيانات، استخدم زر "مسح البيانات" أولاً.
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowSeed(true)}
              disabled={hasData || seedM.isPending}
            >
              <Icon name="plus" size={14} />
              {seedM.isPending ? 'جاري الإنشاء…' : 'إنشاء البيانات التجريبية'}
            </button>
          </div>
        </div>
      </Card>

      {/* Wipe data — danger zone */}
      <Card>
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-[12px] grid place-items-center text-white flex-shrink-0"
            style={{ background: 'linear-gradient(135deg,#EF4444,#B91C1C)' }}
          >
            <Icon name="trash" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-extrabold text-[15px] m-0 text-red">منطقة الخطر — مسح البيانات</h3>
              <Pill variant="danger">لا يمكن التراجع</Pill>
            </div>
            <p className="text-[12.5px] text-ink-2 leading-relaxed mb-2">
              يحذف <b>كل البيانات</b> من النظام: الأطفال، أولياء الأمور، الكروت، الجلسات،
              المنتجات، المخازن، الفواتير، المصاريف، الإيرادات، حركات المخزون،
              أحداث البوابات، الأجهزة، وكل المستخدمين.
            </p>
            <p className="text-[12.5px] text-ink-2 leading-relaxed mb-3">
              <b>يبقى:</b> حساب المدير الحالي فقط، مع الصلاحيات والأدوار وقواعد التسعير
              الافتراضية والقاصات (برصيد صفر) وفئات المصاريف والإيرادات.
            </p>
            <div className="text-[11.5px] text-red-700 bg-red-50 border border-red-200 rounded-[8px] px-3 py-2 mb-3">
              🛑 سيتم تسجيل خروج كل المستخدمين ومسح سجل التدقيق.
            </div>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setShowWipe(true)}
              disabled={!hasData || wipeM.isPending}
            >
              <Icon name="trash" size={14} />
              {wipeM.isPending ? 'جاري المسح…' : 'مسح كل البيانات'}
            </button>
          </div>
        </div>
      </Card>

      {/* Reset system — factory reset, keep super admin only */}
      <Card>
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 rounded-[12px] grid place-items-center text-white flex-shrink-0 text-[22px]"
            style={{ background: 'linear-gradient(135deg,#8B5CF6,#6D28D9)' }}
          >
            ♻️
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-extrabold text-[15px] m-0" style={{ color: '#6D28D9' }}>تصفير النظام بالكامل</h3>
              <Pill variant="danger">للمدير الرئيسي فقط</Pill>
            </div>
            <p className="text-[12.5px] text-ink-2 leading-relaxed mb-2">
              يحذف <b>كل البيانات</b> و<b>كل المستخدمين عدا حساب المدير الرئيسي</b>، ويُعيد
              النظام لوضع البداية. تبقى الأدوار والصلاحيات والقاصات (برصيد صفر) وقواعد
              التسعير والإعدادات العامة (اللوقو).
            </p>
            <div className="text-[11.5px] text-violet-700 bg-violet-50 border border-violet-200 rounded-[8px] px-3 py-2 mb-3">
              🔑 ستُعاد كلمة مرور المدير إلى <b>admin123</b> وسيتم تسجيل خروجك تلقائياً بعد التصفير.
            </div>
            <button
              type="button"
              className="btn text-white"
              style={{ background: '#7C3AED' }}
              onClick={() => setShowReset(true)}
              disabled={resetM.isPending}
            >
              <Icon name="trash" size={14} />
              {resetM.isPending ? 'جاري التصفير…' : 'تصفير النظام بالكامل'}
            </button>
          </div>
        </div>
      </Card>

      <ConfirmSeedModal
        open={showSeed}
        onClose={() => setShowSeed(false)}
        onConfirm={async () => {
          await seedM.mutateAsync(undefined, { onSuccess: () => setShowSeed(false) });
        }}
        loading={seedM.isPending}
      />

      <ConfirmWipeModal
        open={showWipe}
        onClose={() => setShowWipe(false)}
        onConfirm={async () => {
          await wipeM.mutateAsync(undefined, { onSuccess: () => setShowWipe(false) });
        }}
        loading={wipeM.isPending}
      />

      <ConfirmResetModal
        open={showReset}
        onClose={() => setShowReset(false)}
        onConfirm={async () => {
          await resetM.mutateAsync(undefined, { onSuccess: () => setShowReset(false) });
        }}
        loading={resetM.isPending}
      />
    </div>
  );
}
